/**
 * The factory both gateways are built from — PROJECT-OVERVIEW §5.5.
 *
 * **Why a hand-rolled `fetch` wrapper and not axios** (decided 2026-08-17): the
 * three things an HTTP library is usually bought for — retry, dedupe, caching —
 * are TanStack Query's job here, and the fourth, interceptors, is two function
 * calls against `auth/sessionBridge`. That left ~13 kB gzip of production bundle
 * buying a `.create()` call. MSW intercepts `fetch` natively either way.
 *
 * 🔒 **The direction of the import matters.** This module imports the bridge; the
 * bridge imports nothing. If `auth/` ever reached back for the query client, the
 * two clients below would gain a common ancestor and §5.5's split would quietly
 * die — which is precisely the failure this file exists to prevent.
 *
 * 🔒 **`apiUser` and `apiAdm` are two INSTANCES, never one client with a flag.**
 * The gateway split is the coarse half of the authorization story; grupo
 * permissions are the fine half. `eslint.config.js` blocks the wrong import at
 * lint time, and `boundaryDeGateways.test.ts` proves the rule is still armed.
 */
import { notificarSessaoExpirada, obterTokenDeAcesso } from '@/auth/sessionBridge'
import { carregarConfig, type ConfigRuntime } from '@/config/runtimeConfig'

/** Which gateway a client talks to. Passed as a selector so the URL is read at
 *  request time, from the memoised runtime config — never captured at import. */
export type SeletorDeBaseUrl = (config: ConfigRuntime) => string

export interface OpcoesDeRequisicao {
  /** Appended as a query string. `undefined` values are dropped, not sent as "undefined". */
  params?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  headers?: Record<string, string>
}

/**
 * Every failure this client produces, including transport ones.
 *
 * A screen that catches `ErroDeApi` has covered the whole surface; a raw
 * `TypeError: Failed to fetch` leaking into a component is how "algo deu errado"
 * ends up rendered where "sem conexão" belongs.
 */
export class ErroDeApi extends Error {
  /** HTTP status, or **0** when the request never reached the server. */
  readonly status: number
  /** The parsed response body, when there was one. */
  readonly corpo: unknown

  constructor(mensagem: string, status: number, corpo: unknown = null) {
    super(mensagem)
    this.name = 'ErroDeApi'
    this.status = status
    this.corpo = corpo
  }
}

/**
 * Configuração ausente, e não uma falha de rede — subclasse de `ErroDeApi` de
 * propósito, para que tudo que já trata `ErroDeApi` continue tratando esta também.
 *
 * A diferença que ela carrega é a única que importa para a tela: **repetir não
 * resolve**. Um gateway sem endereço em `config.json` vai falhar exatamente igual na
 * segunda tentativa, e oferecer "tentar de novo" transforma um erro de deploy num
 * loop que o usuário culpa a si mesmo.
 */
export class ErroDeConfiguracao extends ErroDeApi {
  /** `api.user` ou `api.adm` — nomeado para a mensagem dizer o que preencher. */
  readonly chave: string

  constructor(chave: string) {
    super(
      `O endereço de \`${chave}\` não está definido em config.json. A aplicação lê essa configuração em runtime (§4.1) e não alcança o gateway sem ela.`,
      0,
    )
    this.name = 'ErroDeConfiguracao'
    this.chave = chave
  }
}

export interface ClienteDeApi {
  get<T = unknown>(caminho: string, opcoes?: OpcoesDeRequisicao): Promise<T>
  post<T = unknown>(caminho: string, corpo?: unknown, opcoes?: OpcoesDeRequisicao): Promise<T>
  put<T = unknown>(caminho: string, corpo?: unknown, opcoes?: OpcoesDeRequisicao): Promise<T>
  patch<T = unknown>(caminho: string, corpo?: unknown, opcoes?: OpcoesDeRequisicao): Promise<T>
  delete<T = unknown>(caminho: string, opcoes?: OpcoesDeRequisicao): Promise<T>
}

function montarUrl(base: string, caminho: string, params: OpcoesDeRequisicao['params']): string {
  const url = new URL(caminho.replace(/^\//, ''), base.endsWith('/') ? base : `${base}/`)
  for (const [chave, valor] of Object.entries(params ?? {})) {
    if (valor !== undefined && valor !== null) url.searchParams.set(chave, String(valor))
  }
  return url.toString()
}

async function lerCorpo(resposta: Response): Promise<unknown> {
  // 204, and any response the server sent without a body.
  if (resposta.status === 204 || resposta.headers.get('content-length') === '0') return null
  const texto = await resposta.text()
  if (texto === '') return null
  try {
    return JSON.parse(texto)
  } catch {
    // Not JSON — a gateway HTML error page, most likely. Hand back the text so the
    // message is diagnosable instead of "Unexpected token < in JSON".
    return texto
  }
}

function mensagemDoCorpo(corpo: unknown, padrao: string): string {
  if (corpo && typeof corpo === 'object' && 'message' in corpo) {
    const mensagem = (corpo as { message: unknown }).message
    if (typeof mensagem === 'string' && mensagem !== '') return mensagem
  }
  return padrao
}

export function criarCliente(seletor: SeletorDeBaseUrl, chave: string): ClienteDeApi {
  async function requisitar<T>(
    metodo: string,
    caminho: string,
    corpo: unknown,
    opcoes: OpcoesDeRequisicao = {},
  ): Promise<T> {
    const config = await carregarConfig()
    const base = seletor(config)

    if (base === '') {
      // The two base URLs ship EMPTY until the gateways are provisioned. Failing
      // here beats resolving `/oportunidades` against the SPA's own origin, which
      // 404s and reads like a bug in the screen.
      //
      // Typed, not just worded: callers need to tell "not configured" from "the
      // gateway is down". They look identical from here and demand opposite
      // responses — one is a deploy to fix, the other is a retry.
      throw new ErroDeConfiguracao(chave)
    }

    const headers = new Headers(opcoes.headers)
    // Asked on EVERY request: the adapter refreshes behind this call, so a token
    // read once at construction would go stale and never recover (0.3 AC-4).
    const resultado = await obterTokenDeAcesso()

    if (resultado.tipo === 'sessaoExpirada') {
      // The refresh already failed and the bridge has already notified. Firing the
      // request anyway would buy a guaranteed 401 and a SECOND notification — QA
      // finding ARCH-003, which measured 2 notifications and 1 wasted round-trip
      // per call. On a screen with five parallel queries that multiplies.
      throw new ErroDeApi('Sua sessão expirou.', 401)
    }
    // `semSessao` is legitimate — send no header at all rather than `Bearer null`.
    if (resultado.tipo === 'token') {
      headers.set('Authorization', `Bearer ${resultado.token}`)
    }
    if (corpo !== undefined) headers.set('Content-Type', 'application/json')

    let resposta: Response
    try {
      resposta = await fetch(montarUrl(base, caminho, opcoes.params), {
        method: metodo,
        headers,
        // Spread rather than `undefined`: `exactOptionalPropertyTypes` treats an
        // explicit `undefined` as a different thing from an absent key.
        ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
        ...(opcoes.signal ? { signal: opcoes.signal } : {}),
      })
    } catch (causa) {
      // An abort is the caller's own doing — do not dress it up as a network fault.
      if (causa instanceof DOMException && causa.name === 'AbortError') throw causa

      /**
       * 🔴 `fetch` rejeita por DOIS motivos que pedem respostas OPOSTAS.
       *
       * 1. O navegador está **offline**. Aí "verifique sua rede" é a instrução
       *    certa e a única que resolve.
       * 2. O navegador está online e **escondeu a resposta** — tipicamente CORS.
       *    O servidor respondeu; o JS não tem como ler status nem corpo, só
       *    recebe a rejeição. Aqui "verifique sua rede" manda a pessoa procurar
       *    exatamente no lugar onde não está o problema.
       *
       * Os dois davam a MESMA frase até 27/08/2026, e custou uma investigação:
       * `2j` e `2n` quebraram em produção com "Sem conexão com o servidor", a
       * rede impecável, e os gateways devolvendo **403
       * `MissingAuthenticationToken`** — a resposta do API Gateway para uma rota
       * que **não existe** — sem cabeçalho de CORS, porque `DEFAULT_4XX`,
       * `ACCESS_DENIED` e `UNAUTHORIZED` estão com `responseParameters` vazio.
       *
       * 🔒 Nomear o gateway segue a mesma regra do `ErroDeConfiguracao` logo
       * acima: dizer QUAL endereço falhou é o que transforma meia hora de
       * investigação numa correção óbvia de deploy.
       */
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false
      if (offline) {
        throw new ErroDeApi('Sem conexão com o servidor. Verifique sua rede e tente novamente.', 0)
      }

      throw new ErroDeApi(
        `Não foi possível falar com \`${chave}\` (${base}). Sua rede está ativa, ` +
          'então o endereço pode não estar respondendo, ou respondeu e o navegador ' +
          'bloqueou a resposta (CORS). Avise o time da Lascasas.',
        0,
      )
    }

    const conteudo = await lerCorpo(resposta)

    if (resposta.status === 401) {
      // 🔒 ONE place, for BOTH clients (AC-3). The bridge drops the user at /login
      // with a readable pt-BR message instead of a raw 401 rendered into a screen.
      // NOT 403: that means "signed in, not allowed" (BR-17 — the server is the
      // control), and logging the user out would be a baffling answer to it.
      notificarSessaoExpirada()
      throw new ErroDeApi(mensagemDoCorpo(conteudo, 'Sua sessão expirou.'), 401, conteudo)
    }

    if (!resposta.ok) {
      throw new ErroDeApi(
        mensagemDoCorpo(conteudo, `A requisição falhou (${resposta.status}).`),
        resposta.status,
        conteudo,
      )
    }

    return conteudo as T
  }

  return {
    get: (caminho, opcoes) => requisitar('GET', caminho, undefined, opcoes),
    post: (caminho, corpo, opcoes) => requisitar('POST', caminho, corpo, opcoes),
    put: (caminho, corpo, opcoes) => requisitar('PUT', caminho, corpo, opcoes),
    patch: (caminho, corpo, opcoes) => requisitar('PATCH', caminho, corpo, opcoes),
    delete: (caminho, opcoes) => requisitar('DELETE', caminho, undefined, opcoes),
  }
}
